import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from 'semantic-ui-react';
import styled from 'styled-components';

const PageWrapper = styled.div`
  padding-top: 4em;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 4em 1.5em 3em;
  max-width: 700px;
  margin: 0 auto;
`;

const Logo = styled.img`
  height: 56px;
  margin-bottom: 1em;
`;

const Tagline = styled.h1`
  font-size: 2em;
  font-weight: 300;
  color: #333;
  margin: 0 0 0.5em;
  line-height: 1.3;
`;

const Subtitle = styled.p`
  font-size: 1.2em;
  color: #666;
  max-width: 500px;
  margin: 0 auto 2em;
  line-height: 1.6;
`;

const CTAButton = styled(Button)`
  &&& {
    border-radius: 20px;
    padding: 0.9em 2.5em;
    font-size: 1.1em;
    box-shadow: 0 2px 8px rgba(33, 133, 208, 0.3);
    transition: box-shadow 0.2s, transform 0.2s;

    &:hover {
      box-shadow: 0 4px 16px rgba(33, 133, 208, 0.4);
      transform: translateY(-1px);
    }
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  max-width: 600px;
  margin: 0 auto;
`;

const StepsSection = styled.section`
  padding: 3em 1.5em;
  max-width: 800px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 1.6em;
  font-weight: 400;
  color: #333;
  margin-bottom: 2em;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5em;
`;

const StepCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5em;
  padding: 1.5em;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const StepNumber = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #2185d0;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  font-weight: 600;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: 1.2em;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.4em;
`;

const StepDescription = styled.p`
  font-size: 1em;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const FeatureSection = styled.section`
  padding: 3em 1.5em;
  max-width: 800px;
  margin: 0 auto;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5em;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 1.5em 1em;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  }
`;

const FeatureIcon = styled.div`
  margin-bottom: 0.75em;

  img {
    width: 32px;
    height: 32px;
    opacity: 0.7;
  }
`;

const FeatureTitle = styled.h4`
  font-size: 1.05em;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.3em;
`;

const FeatureDescription = styled.p`
  font-size: 0.95em;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const FooterCTA = styled.section`
  text-align: center;
  padding: 3em 1.5em 4em;
`;

const FooterText = styled.p`
  font-size: 1.1em;
  color: #666;
  margin-bottom: 1.5em;
`;

const steps = [
  {
    number: 1,
    title: 'Create a list',
    description: 'Start by describing what needs to get done. You can type it out or let AI generate a task list from a photo or description.',
  },
  {
    number: 2,
    title: 'Delegate to others',
    description: 'Share your list with the people responsible. They get a simple link \u2014 no account needed to view and check off tasks.',
  },
  {
    number: 3,
    title: 'Track progress',
    description: 'Watch tasks get checked off in real time. Everyone stays on the same page without extra messages or follow-ups.',
  },
];

const features = [
  {
    icon: '/ai_icon_outline.svg',
    title: 'AI-powered',
    description: 'Snap a photo or describe a job and get a ready-made task list.',
  },
  {
    icon: '/share-alt-square.svg',
    title: 'Easy sharing',
    description: 'Share via link or QR code. No sign-up required to participate.',
  },
  {
    icon: '/duplicate.svg',
    title: 'Templates',
    description: 'Save lists as templates for tasks you repeat often.',
  },
];

const LandingPage = ({ user, onLogin, onCreateTask }) => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <HeroSection>
        <Logo src="/didit_logo_bold.svg" alt="Didit" />
        <Tagline>The task list for other people</Tagline>
        <Subtitle>
          Create tasks, delegate them to the right people, and track progress
          as things get done &mdash; all in one place.
        </Subtitle>
        {user ? (
          <CTAButton primary size="large" onClick={() => navigate('/owned')}>
            <Icon name="list" />
            Your Task Lists
          </CTAButton>
        ) : (
          <CTAButton primary size="large" onClick={onLogin}>
            <Icon name="google" />
            Sign in with Google
          </CTAButton>
        )}
      </HeroSection>

      <Divider />

      <StepsSection>
        <SectionTitle>How it works</SectionTitle>
        <StepsContainer>
          {steps.map((step) => (
            <StepCard key={step.number}>
              <StepNumber>{step.number}</StepNumber>
              <StepContent>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepContent>
            </StepCard>
          ))}
        </StepsContainer>
      </StepsSection>

      <Divider />

      <FeatureSection>
        <SectionTitle>Built to keep things simple</SectionTitle>
        <FeatureGrid>
          {features.map((feature, i) => (
            <FeatureCard key={i}>
              <FeatureIcon>
                <img src={feature.icon} alt={feature.title} />
              </FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeatureSection>

      <Divider />

      <FooterCTA>
        <FooterText>
          {user
            ? 'Ready to get things moving?'
            : 'Get started in seconds \u2014 no credit card, no setup.'}
        </FooterText>
        {user ? (
          <CTAButton primary size="large" onClick={onCreateTask}>
            <Icon name="plus" />
            Create a Task List
          </CTAButton>
        ) : (
          <CTAButton primary size="large" onClick={onLogin}>
            <Icon name="google" />
            Sign in with Google
          </CTAButton>
        )}
      </FooterCTA>
    </PageWrapper>
  );
};

export default LandingPage;
